import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import NextLink from 'next/link';
import { updateEnvironmentHash } from 'tradera-state/environment/actions';
import { useIsFeatureEnabled } from 'tradera-hooks/use-is-feature-enabled';
import { isNextJs } from 'tradera-utils/nextjs';

const isVipLink = href => href.startsWith('/item/');

const isLinkToNextWebPage = href =>
    href.endsWith('?next=1') ||
    href.endsWith('/how-to-buy') ||
    href.endsWith('/how-to-sell');

const ALink = ({
    useLink,
    href: givenHref,
    children,
    onClick: givenOnClick,
    ...otherProps
}) => {
    const dispatch = useDispatch();
    const isSpaNavigationEnabled = useSelector(
        state => state.environment.isSpaNavigationEnabled
    );
    const isNextWebVipSpaNavigationEnabled = useIsFeatureEnabled(
        'next-web-vip-spa-navigation'
    );
    const href =
        isNextWebVipSpaNavigationEnabled && isVipLink(givenHref)
            ? `${givenHref}?next=1`
            : givenHref;

    const handleSpaNavigationClick = useCallback(
        evt => {
            dispatch(updateEnvironmentHash());
            if (givenOnClick) givenOnClick(evt);
        },
        [dispatch, givenOnClick]
    );
    if (isNextJs && isSpaNavigationEnabled && isLinkToNextWebPage(href)) {
        // NextJS routing
        return (
            <NextLink href={href} prefetch={false}>
                <a
                    {...otherProps}
                    onClick={handleSpaNavigationClick}
                    onMouseEnter={e => e.stopPropagation()}>
                    {children}
                </a>
            </NextLink>
        );
    } else if (!isNextJs && isSpaNavigationEnabled && useLink) {
        // react-router-dom
        // Strip away origin for local links
        let to = href;
        if (href?.startsWith(location.origin)) {
            const { pathname, search } = new URL(href);
            to = pathname + search;
        }
        return (
            <Link to={to} {...otherProps} onClick={handleSpaNavigationClick}>
                {children}
            </Link>
        );
    }
    return (
        <a href={href} onClick={givenOnClick} {...otherProps}>
            {children}
        </a>
    );
};

ALink.propTypes = {
    useLink: PropTypes.bool,
    href: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    onClick: PropTypes.func
};

ALink.defaultProps = {
    useLink: false,
    onClick: null
};

export default ALink;
